import { EMPTY, forkJoin, merge, of, throwError } from 'rxjs';
import { catchError, first, map, mergeMap, retry, switchMap, tap } from 'rxjs/operators';

import { fetchNdJson } from './http.js';
import { buildPrompt } from './prompts.js';
import { generate, maxContextWindow, researchModel } from './gpt.js';

export function parallelInternetResearch(messages, queries, summarizingQuestion=null) {
    let visitedUrls = new Set();
    return merge(...queries.map(fetchInternetResearch)).pipe(
        mergeMap(page => {
            let href = page.href;
            if (visitedUrls.has(href)) {
                return EMPTY;
            }
            visitedUrls.add(href);
            return extractRelevantInformation(page.content, [summarizingQuestion], 0).pipe(
                map(extract => ({
                    ...page,
                    extract,
                })),
            );
        }),
    );
}

function fetchInternetResearch(query) {
    return of(undefined).pipe(
        switchMap(() => fetchNdJson(`/api/internet-research?q=${encodeURIComponent(query)}`)),
        retry({
            count: 3,
            delay: 3000 + 1000*Math.random(),
        }),
    );
}

export function extractRelevantInformation(content, queries, securityLevel=100) {
    return of(undefined).pipe(
        switchMap(() => forkJoin([
            buildPrompt('empty-set'),
            buildPrompt('extract-query-info', {
                query: queries.map(q => `- ${q}\n`).join(''),
                content,
            }),
        ])),
        switchMap(([emptySet, prompt]) => generate(prompt, {
            model: researchModel,
            balancer_options: {
                security_level: securityLevel,
            },
            options: {
                temperature: 1,
                num_ctx: maxContextWindow,
            },
        }).pipe(
            catchError(err => {
                if (err?.status === 500) {
                    // some content blows up the llm. we just ignore
                    // that content when we stumble over it during
                    // content extraction.
                    console.warn('Cannot extract relevant information because of generate error.', prompt, err);
                    return EMPTY;
                }
                return throwError(err);
            }),
            map(resp => resp.response),
            map(content => {
                if (content.length < 256 && content.indexOf(emptySet) !== -1) {
                    // drop content that cannot be answered well
                    return '';
                }
                return content;
            }),
        )),
    );
}
